import styles from "./JourneyProgressCircle.module.css";

interface JourneyProgressCircleProps {
	/** Current completed count (e.g., 2 out of 7). Renders as a percentage. */
	current: number;
	/** Total count. Must be > 0. */
	total: number;
	/**
	 * Localized aria-label override. When omitted, falls back to the English
	 * "N% complete" — callers in i18n contexts should pass the resolved string
	 * from their translation namespace.
	 */
	ariaLabel?: string;
}

const SIZE = 44;
const CENTER = SIZE / 2;
const STROKE = 5;
const STROKE_R = (SIZE - STROKE) / 2;
const C = 2 * Math.PI * STROKE_R;

export function JourneyProgressCircle({
	current,
	total,
	ariaLabel,
}: JourneyProgressCircleProps) {
	const safeTotal = total > 0 ? total : 1;
	const safeCurrent = Math.max(0, Math.min(current, safeTotal));
	const fraction = safeCurrent / safeTotal;
	const dashOffset = C - fraction * C;
	const resolvedAriaLabel =
		ariaLabel ?? `${Math.round(fraction * 100)}% complete`;

	return (
		// dir="ltr" forces the percentage digits to render left-to-right even under RTL (D-31)
		<span className={styles.wrapper} dir="ltr" aria-hidden="false">
			<svg
				className={styles.svg}
				viewBox={`0 0 ${SIZE} ${SIZE}`}
				role="img"
				aria-label={resolvedAriaLabel}
			>
				<defs>
					<linearGradient
						id="journey-pc-track"
						x1="50%"
						y1="0%"
						x2="50%"
						y2="100%"
					>
						<stop offset="0%" stopColor="#d6d8e8" />
						<stop offset="50%" stopColor="#eff0fb" />
						<stop offset="100%" stopColor="#eff0fb" />
					</linearGradient>
				</defs>
				<circle
					cx={CENTER}
					cy={CENTER}
					r={STROKE_R}
					fill="none"
					stroke="url(#journey-pc-track)"
					strokeWidth={STROKE}
				/>
				<circle
					cx={CENTER}
					cy={CENTER}
					r={STROKE_R}
					fill="none"
					stroke="#5e60bf"
					strokeWidth={STROKE}
					strokeLinecap="round"
					strokeDasharray={C}
					strokeDashoffset={dashOffset}
					transform={`rotate(-90 ${CENTER} ${CENTER})`}
				/>
				<text
					className={styles.digits}
					x={CENTER}
					y={CENTER + 4}
					textAnchor="middle"
				>
					{Math.round(fraction * 100)}%
				</text>
			</svg>
		</span>
	);
}
